import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {RoomOfferPriceBreakdown} from 'components/Accommodation/RoomOfferCardPriceInfo'
import {
  getAnchorPriceNightlyPerRoom,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'
import {RootState} from 'store'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {getRoomOfferPrices} from '@vio/core/src/price/business/roomOfferPrices'
import {getRoundingStrategy} from '@vio/core/src/utils/number'
import {Price} from '@vio/shared/src/search/Price'

import {
  priceSuffixMessages,
  RoomOfferPriceDataId
} from '../RoomOfferPrice/constants'

type Props = {
  hotelId: string
  prices: OfferPrice[]
  isOfferSoldOut?: boolean
  isRightAligned?: boolean
}

const BasePriceRow = styled.div`
  display: flex;
  white-space: nowrap;
  align-items: baseline;
  justify-content: flex-end;
  gap: ${cssTheme.layout.spacing.s200};
`

const roundingStrategy = getRoundingStrategy()

const PriceSuffix = styled(Text)<{isOfferSoldOut?: boolean}>`
  white-space: nowrap;
  margin-left: ${cssTheme.layout.spacing.s200};
  ${({isOfferSoldOut}) =>
    isOfferSoldOut &&
    `
    color: ${cssTheme.colors.content.neutral.disabled};
  `}
`

export const RoomsOfferPrice = ({
  hotelId,
  prices,
  isOfferSoldOut = false,
  isRightAligned = false
}: Props) => {
  const {formatMessage} = useIntl()
  const {offerEntity} = useAccommodationTopOffers({
    hotelId
  })
  const anchorPrice = useSelector((state: RootState) =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)
  const showTotalPrices = useSelector(getShowTotalPrices)

  // A split booking is one combined price, so it is never multiplied by rooms
  const roomPrices = getRoomOfferPrices(prices, {
    numberOfRooms: 1,
    anchorNightly: anchorPrice,
    anchorTotal: anchorPriceTotal
  })
  if (!roomPrices) return null

  const {currencyCode} = roomPrices
  const mainPrice = showTotalPrices ? roomPrices.total : roomPrices.nightly
  const detailsPrice = showTotalPrices ? roomPrices.nightly : roomPrices.total

  const mainPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.Total
    : RoomOfferPriceDataId.PerNight

  const detailsPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.PerNight
    : RoomOfferPriceDataId.Total

  const soldOutColor = isOfferSoldOut
    ? cssTheme.colors.content.neutral.disabled
    : null

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      alignItems={isRightAligned ? 'flex-end' : 'flex-start'}
    >
      <BasePriceRow>
        {mainPrice.showAnchor ? (
          <Price
            isAnchor
            currency={currencyCode}
            value={mainPrice.anchor}
            roundingStrategy={roundingStrategy}
            style={{color: soldOutColor}}
            dataId="splitBooking.anchorPrice"
          />
        ) : null}

        <Price
          currency={currencyCode}
          value={mainPrice.price}
          roundingStrategy={roundingStrategy}
          style={{color: soldOutColor}}
          size="lg"
          dataId={mainPriceDataId}
        />
        {!showTotalPrices && (
          <PriceSuffix variant="bodyS" isOfferSoldOut={isOfferSoldOut}>
            {formatMessage(priceSuffixMessages.perNight)}
          </PriceSuffix>
        )}
      </BasePriceRow>

      <RoomOfferPriceBreakdown
        isSplitBooking
        hotelId={hotelId}
        detailsPriceValue={detailsPrice.price}
        currencyCode={currencyCode}
        roundingStrategy={roundingStrategy}
        detailsPriceDataId={detailsPriceDataId}
        showTotalPrices={showTotalPrices}
        isOfferSoldOut={isOfferSoldOut}
        numberOfRooms={1}
      />
    </Box>
  )
}
